import { Box, ButtonBase, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { IS_WEB } from "../../utils/runtime";

interface WebBackButtonProps {
  to?: string;
  /** Короткий текст рядом со стрелкой в legacy-режиме (без `title`). */
  label?: string;
  /**
   * Заголовок страницы. Если задан, компонент рендерит header:
   * [круглая кнопка-стрелка] [заголовок] на одной строке (веб),
   * либо только заголовок (в Telegram, где back показывает нативный BackButton).
   */
  title?: string;
  onClick?: () => void;
}

/**
 * Видимая кнопка «Назад» для веб-версии.
 *
 * Два режима:
 * - `title` не задан — рендерится отдельная pill-кнопка со стрелкой (веб-only).
 *   В Telegram — не рендерится (там нативная BackButton).
 * - `title` задан — рендерится строка [кнопка] [заголовок]. В Telegram — только заголовок.
 */
export function WebBackButton({ to, label, title, onClick }: WebBackButtonProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
    } else {
      navigate(-1 as any);
    }
  };

  if (title) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          mt: 1,
          mb: "20px",
          minHeight: 32,
        }}
      >
        {IS_WEB && (
          <ButtonBase
            onClick={handleClick}
            aria-label="back"
            sx={(theme) => ({
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.06)"
                  : "rgba(0, 0, 0, 0.05)",
              color: theme.palette.text.primary,
              transition: "background 0.15s, transform 0.1s",
              "&:hover": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.10)"
                    : "rgba(0, 0, 0, 0.08)",
              },
              "&:active": { transform: "scale(0.94)" },
            })}
          >
            <Icon icon="solar:arrow-left-linear" width={16} height={16} />
          </ButtonBase>
        )}
        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 600,
            color: "text.primary",
            flex: 1,
            minWidth: 0,
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  }

  if (!IS_WEB) return null;

  return (
    <Box sx={{ width: "100%", mt: 1, mb: 1.5 }}>
      <ButtonBase
        onClick={handleClick}
        aria-label="back"
        sx={(theme) => ({
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: label ? "6px" : 0,
          width: label ? "auto" : 32,
          height: 32,
          padding: label ? "0 12px 0 10px" : 0,
          borderRadius: label ? "16px" : "50%",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.05)",
          color: theme.palette.text.primary,
          transition: "background 0.15s, transform 0.1s",
          "&:hover": {
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.10)"
                : "rgba(0, 0, 0, 0.08)",
          },
          "&:active": { transform: "scale(0.94)" },
        })}
      >
        <Icon icon="solar:arrow-left-linear" width={16} height={16} />
        {label && (
          <Typography
            sx={{ fontSize: "13px", fontWeight: 500, color: "inherit" }}
          >
            {label}
          </Typography>
        )}
      </ButtonBase>
    </Box>
  );
}
